import type { ChecklistItem as EmbeddedChecklistItem } from '../../types/timeline'
import type { TimelineElement } from '../../context/TimelineContext'
import type { CalendarBoardFilterState, ChecklistItem as FlatChecklistItem } from '../../types/schema'
import type { MeetingRecord } from '../../types/meetings'
import type { HuddleSession } from '../../services/huddleSessionService'
import { formatOccurrenceTitle } from '../../services/recurringChecklistService'
import { getChecklistScheduleSpan } from '../../services/checklistDurationHelpers'
import { formatZonedDateKey, resolveWorkspaceTimeZone } from '../../services/repeat/keys'

export type CalendarEventKind = 'goal' | 'task' | 'subtask' | 'checklist' | 'meeting' | 'huddle' | 'session' | 'edu_event' | 'edu_ptm'

export interface CalendarEvent {
  id: string
  kind: CalendarEventKind
  title: string
  /** ISO date (yyyy-mm-dd) inclusive. */
  startDate: string
  /** ISO date (yyyy-mm-dd) inclusive. */
  endDate: string
  /** Optional time-of-day window. Both required for a timed event. */
  startTime?: string | null
  endTime?: string | null
  /** Source ids — for click + drag wiring. */
  elementId?: string
  checklistItemId?: string
  /** Owning element so checklist edits know who to update. */
  parentElementId?: string
  /** Meeting source id for read-only meeting entries. */
  meetingId?: string
  /** Ad-hoc huddle source id for historical presence sessions. */
  huddleSessionId?: string
  /** Scheduled-meeting provider, retained so scheduled huddles stay meetings. */
  sourceProvider?: MeetingRecord['sourceProvider']
  /** A huddle with no end timestamp is still live. */
  isOngoing?: boolean
  /** Goal id this event ultimately rolls up to (for filter cascade + grouping). */
  rootGoalId: string | null
  /** Immediate parent in the tree (Goal id for tasks, Task id for subtasks, etc.). */
  parentId: string | null
  /** Board the source lives in. */
  board: string
  /** True when this event was materialized from a recurring template. Views
   * can decorate it with a ↻ glyph. */
  isRecurring?: boolean
  /** Occurrence key (YYYY-MM-DD) for the recurring instance, if any. */
  recurrenceOccurrenceKey?: string
  /** Owning recurring template id, if any. */
  recurrenceTemplateId?: string
  /** Education series ID when kind is 'session'. */
  eduSeriesId?: string
  /** Education session ISO date (YYYY-MM-DD) when kind is 'session'. */
  eduSessionDate?: string
  /** Education session occurrence payload. */
  eduOccurrence?: unknown
  /** Education series payload. */
  eduSeries?: unknown
  /** Education event ID when kind is 'edu_event'. */
  eduEventId?: string
  /** Education event payload when kind is 'edu_event'. */
  eduEvent?: unknown
  /** PTM day ID when kind is 'edu_ptm'. */
  ptmDayId?: string
  /** PTM day payload when kind is 'edu_ptm'. */
  ptmDay?: unknown
  /** PTM slot payload when kind is 'edu_ptm'. */
  ptmSlot?: unknown
  /** PTM teacher UID when kind is 'edu_ptm'. */
  ptmTeacherUid?: string
  /** True when the event was cancelled. */
  isCancelled?: boolean
}

const pad2 = (value: number) => value.toString().padStart(2, '0')

export const toIsoDate = (date: Date, timeZone?: string): string => {
  if (timeZone) {
    return formatZonedDateKey(date, resolveWorkspaceTimeZone(timeZone))
  }
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`
}

export const fromIsoDate = (iso: string): Date => {
  const [y, m, d] = iso.split('-').map((value) => Number.parseInt(value, 10))
  return new Date(y, (m ?? 1) - 1, d ?? 1)
}

export const normalizeTimelineDate = (value: unknown): string | null => {
  if (!value) return null
  if (typeof value === 'string') {
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10)
    const parsed = new Date(value)
    return Number.isNaN(parsed.getTime()) ? null : toIsoDate(parsed)
  }
  if (typeof value === 'number') {
    const parsed = new Date(value)
    return Number.isNaN(parsed.getTime()) ? null : toIsoDate(parsed)
  }
  return null
}

const isTimestampLike = (value: unknown): value is { toDate: () => Date } =>
  typeof value === 'object' && value !== null && 'toDate' in value && typeof (value as { toDate?: unknown }).toDate === 'function'

export const normalizeTimestampDate = (value: unknown): Date | null => {
  if (!value) return null
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value
  if (isTimestampLike(value)) {
    const date = value.toDate()
    return Number.isNaN(date.getTime()) ? null : date
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value)
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }
  return null
}

export const addDays = (date: Date, days: number): Date => {
  const next = new Date(date)
  next.setDate(next.getDate() + days)
  return next
}

export const startOfWeek = (date: Date): Date => {
  const next = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  next.setDate(next.getDate() - next.getDay())
  return next
}

export const startOfMonthGrid = (date: Date): Date => {
  const first = new Date(date.getFullYear(), date.getMonth(), 1)
  return startOfWeek(first)
}

export const endOfMonthGrid = (date: Date): Date => {
  const last = new Date(date.getFullYear(), date.getMonth() + 1, 0)
  const end = startOfWeek(last)
  end.setDate(end.getDate() + 6)
  return end
}

export const daysInRange = (start: Date, end: Date): Date[] => {
  const out: Date[] = []
  const cursor = new Date(start.getFullYear(), start.getMonth(), start.getDate())
  const limit = new Date(end.getFullYear(), end.getMonth(), end.getDate())
  while (cursor.getTime() <= limit.getTime()) {
    out.push(new Date(cursor))
    cursor.setDate(cursor.getDate() + 1)
  }
  return out
}

export const sameIsoDate = (a: string, b: string) => a.slice(0, 10) === b.slice(0, 10)

export const compareIsoDate = (a: string, b: string) => a.localeCompare(b)

export const isChecklistScheduled = (item: EmbeddedChecklistItem): boolean =>
  getChecklistScheduleSpan(item) !== null

/**
 * Build calendar events from the timeline element graph.
 *
 * Goals/Tasks/SubTasks contribute one event each (using startDate/endDate).
 * Each element's embedded `checklist` contributes one event per dated item.
 *
 * Multi-day items are emitted with their full span; the views handle splitting
 * across week rows.
 */
export const buildEventsFromElements = (
  elements: TimelineElement[],
  acceptedBoards: Set<string>,
): CalendarEvent[] => {
  const byId = new Map(elements.map((element) => [element.id, element]))
  const out: CalendarEvent[] = []

  const rollupRootGoalId = (element: TimelineElement): string | null => {
    if (element.type === 'GOAL') return element.id
    let cursor: TimelineElement | undefined = element
    while (cursor && cursor.type !== 'GOAL') {
      if (!cursor.parentId) return null
      cursor = byId.get(cursor.parentId)
    }
    return cursor?.id ?? null
  }

  for (const element of elements) {
    if (!acceptedBoards.has(element.board)) continue

    const start = normalizeTimelineDate(element.startDate)
    const end = normalizeTimelineDate(element.endDate) ?? start
    const rootGoalId = rollupRootGoalId(element)

    if (start && end) {
      out.push({
        id: `element-${element.id}`,
        kind: element.type === 'GOAL' ? 'goal' : element.type === 'TASK' ? 'task' : 'subtask',
        title: element.title,
        startDate: start,
        endDate: end,
        elementId: element.id,
        rootGoalId,
        parentId: element.parentId,
        board: element.board,
      })
    }

    for (const item of element.checklist ?? []) {
      if (!isChecklistScheduled(item)) continue
      const span = getChecklistScheduleSpan(item)
      if (!span) continue
      out.push({
        id: `checklist-${element.id}-${item.id}`,
        kind: 'checklist',
        title: item.text || 'Untitled checklist',
        startDate: span.startDate,
        endDate: span.endDate,
        startTime: item.hasTimeWindow ? (item.scheduleTimeFrom ?? null) : null,
        endTime: item.hasTimeWindow ? (item.scheduleTimeTo ?? null) : null,
        checklistItemId: item.id,
        parentElementId: element.id,
        rootGoalId,
        parentId: element.id,
        board: element.board,
      })
    }
  }

  return out
}

/** Build calendar events from flat `checklist_items` docs that were
 * materialized by the recurring-checklist cron. `buildEventsFromElements`
 * only reads the legacy embedded `element.checklist`; recurrence instances
 * don't live there, so this builder is the bridge. */
export const buildEventsFromRecurringInstances = (
  items: FlatChecklistItem[],
  acceptedBoards: Set<string>,
): CalendarEvent[] => {
  const out: CalendarEvent[] = []
  for (const item of items) {
    if (item.isRecurrenceInstance !== true) continue
    const board = item.board ?? 'DASHBOARD'
    if (!acceptedBoards.has(board)) continue
    const span = getChecklistScheduleSpan(item)
    if (!span) continue
    out.push({
      id: `recurring-${item.id}`,
      kind: 'checklist',
      // Composite `<title>_<key>` so a dated instance is unambiguous in the
      // calendar cell / event popover — MAIN items pass through unchanged.
      title: formatOccurrenceTitle(
        { title: item.title || 'Recurring step', checklistType: item.checklistType ?? 'DAILY' },
        item.occurrenceKey ?? null,
      ),
      startDate: span.startDate,
      endDate: span.endDate,
      startTime: item.hasTimeWindow ? (item.scheduleTimeFrom ?? null) : null,
      endTime: item.hasTimeWindow ? (item.scheduleTimeTo ?? null) : null,
      checklistItemId: item.id,
      parentElementId: undefined,
      rootGoalId: null,
      parentId: null,
      board,
      isRecurring: true,
      recurrenceOccurrenceKey: item.occurrenceKey ?? undefined,
      recurrenceTemplateId: item.recurrenceTemplateId ?? undefined,
    })
  }
  return out
}

export const buildEventsFromMeetings = (meetings: MeetingRecord[]): CalendarEvent[] => meetings.flatMap((meeting) => {
  const start = normalizeTimestampDate(meeting.scheduledAt)
  if (!start) return []
  const durationMinutes = Math.max(15, Number(meeting.durationMinutes) || 30)
  const end = new Date(start.getTime() + durationMinutes * 60_000)
  return [{
    id: `meeting-${meeting.id}`,
    kind: 'meeting' as const,
    title: meeting.title || 'Untitled meeting',
    startDate: toIsoDate(start),
    endDate: toIsoDate(end),
    startTime: formatTimeMinutes(start.getHours() * 60 + start.getMinutes()),
    endTime: formatTimeMinutes(end.getHours() * 60 + end.getMinutes()),
    meetingId: meeting.id,
    sourceProvider: meeting.sourceProvider,
    rootGoalId: null,
    parentId: null,
    board: 'DASHBOARD',
  }]
})

export const buildEventsFromHuddleSessions = (sessions: HuddleSession[]): CalendarEvent[] => sessions.flatMap((session) => {
  const start = normalizeTimestampDate(session.startedAt)
  if (!start) return []
  const endedAt = normalizeTimestampDate(session.endedAt)
  const end = endedAt ?? new Date()
  return [{
    id: `huddle-${session.id}`,
    kind: 'huddle' as const,
    title: session.channelName || session.channelId,
    startDate: toIsoDate(start),
    endDate: toIsoDate(end),
    startTime: formatTimeMinutes(start.getHours() * 60 + start.getMinutes()),
    endTime: formatTimeMinutes(end.getHours() * 60 + end.getMinutes()),
    huddleSessionId: session.id,
    isOngoing: session.endedAt === null,
    rootGoalId: null,
    parentId: null,
    board: 'DASHBOARD',
  }]
})

export interface BuildEventsFromEduSessionsOptions {
  elements?: TimelineElement[] | Map<string, TimelineElement>
  directoryMembers?: Array<{ uid: string; displayName?: string; name?: string }>
  t?: (key: string, fallback?: string) => string
}

export interface EduSessionOccurrenceLike {
  id?: string
  seriesId?: string
  date: string
  startTime: string
  durationMin: number
  nodeId?: string
  classId?: string
  teacherUids?: string[]
  status?: string
  substituteUid?: string | null
  substituteName?: string | null
  title?: string
  roomId?: string | null
  onlineUrl?: string | null
  topicItemIds?: string[]
  note?: string | null
}

export const buildEventsFromEduSessions = (
  occurrences: EduSessionOccurrenceLike[],
  options?: BuildEventsFromEduSessionsOptions,
): CalendarEvent[] => {
  const elementsMap = new Map<string, TimelineElement>()
  if (options?.elements) {
    if (options.elements instanceof Map) {
      options.elements.forEach((v, k) => elementsMap.set(k, v))
    } else if (Array.isArray(options.elements)) {
      for (const el of options.elements) {
        if (el?.id) elementsMap.set(el.id, el)
      }
    }
  }

  const membersMap = new Map<string, string>()
  if (options?.directoryMembers) {
    for (const m of options.directoryMembers) {
      if (m?.uid) {
        membersMap.set(m.uid, m.displayName || m.name || m.uid)
      }
    }
  }

  return occurrences.flatMap((occ) => {
    if (!occ.date || !occ.startTime) return []

    const [h, m] = occ.startTime.split(':').map(Number)
    const startMins = (h || 0) * 60 + (m || 0)
    const endMins = startMins + Math.max(1, occ.durationMin || 30)
    const endH = Math.floor(endMins / 60) % 24
    const endM = endMins % 60
    const endTime = `${pad2(endH)}:${pad2(endM)}`

    let baseTitle = occ.title
    if (!baseTitle && occ.nodeId) {
      const nodeEl = elementsMap.get(occ.nodeId)
      const classEl = occ.classId ? elementsMap.get(occ.classId) : undefined
      if (nodeEl?.title && classEl?.title) {
        baseTitle = `${nodeEl.title} (${classEl.title})`
      } else if (nodeEl?.title) {
        baseTitle = nodeEl.title
      } else if (classEl?.title) {
        baseTitle = classEl.title
      }
    }
    if (!baseTitle) {
      baseTitle = options?.t
        ? options.t('NLSEducationCalendarClassSessionFallback', 'Class session')
        : 'Class session'
    }

    const isCancelled = occ.status === 'CANCELLED'
    let subName = occ.substituteName
    if (!subName && occ.substituteUid) {
      subName = membersMap.get(occ.substituteUid) || occ.substituteUid
    }

    let displayTitle = baseTitle
    if (isCancelled) {
      displayTitle = `[Cancelled] ${displayTitle}`
    }
    if (subName) {
      displayTitle = `${displayTitle} (Sub: ${subName})`
    }

    const eventId = occ.id
      ? `session-${occ.id}`
      : `session-${occ.seriesId || 'series'}_${occ.date}`

    return [
      {
        id: eventId,
        kind: 'session' as const,
        title: displayTitle,
        startDate: occ.date,
        endDate: occ.date,
        startTime: occ.startTime,
        endTime,
        elementId: occ.nodeId,
        parentElementId: occ.classId,
        rootGoalId: null,
        parentId: occ.classId || null,
        board: 'DASHBOARD',
        eduSeriesId: occ.seriesId,
        eduSessionDate: occ.date,
        eduOccurrence: occ,
      },
    ]
  })
}

/**
 * Cascade-resolve effective visibility for the filter tree.
 *
 * Walking from the root down: if an id has an explicit toggle, use it; otherwise
 * inherit from the nearest explicit ancestor; if none, fall back to `showAll`.
 */
export const resolveVisibility = (
  ancestorIds: string[],
  selfId: string,
  state: CalendarBoardFilterState,
): boolean => {
  if (Object.prototype.hasOwnProperty.call(state.explicit, selfId)) {
    return state.explicit[selfId]
  }
  for (let i = ancestorIds.length - 1; i >= 0; i--) {
    const ancestor = ancestorIds[i]
    if (Object.prototype.hasOwnProperty.call(state.explicit, ancestor)) {
      return state.explicit[ancestor]
    }
  }
  return state.showAll
}

export const HOUR_ROW_HEIGHT = 48
export const HOURS = Array.from({ length: 24 }, (_, idx) => idx)

export const parseTimeMinutes = (value: string | null | undefined): number | null => {
  if (!value) return null
  const match = /^(\d{1,2}):(\d{2})/.exec(value)
  if (!match) return null
  return Number.parseInt(match[1], 10) * 60 + Number.parseInt(match[2], 10)
}

export const formatTimeMinutes = (minutes: number): string => {
  const hours = Math.floor((minutes % (24 * 60)) / 60)
  const mins = minutes % 60
  return `${pad2(hours)}:${pad2(mins)}`
}

export const eventColorVar = (kind: CalendarEventKind): string => `var(--cal-${kind})`

const MONTH_FORMATTER = new Intl.DateTimeFormat('en', { month: 'long', year: 'numeric' })
const DAY_FORMATTER = new Intl.DateTimeFormat('en', {
  weekday: 'long',
  month: 'short',
  day: 'numeric',
  year: 'numeric',
})
const WEEK_RANGE_FORMATTER = new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric' })

export const formatRangeLabel = (
  range: 'day' | 'week' | 'month',
  focused: Date,
): string => {
  if (range === 'day') return DAY_FORMATTER.format(focused)
  if (range === 'month') return MONTH_FORMATTER.format(focused)
  const weekStart = startOfWeek(focused)
  const weekEnd = addDays(weekStart, 6)
  return `${WEEK_RANGE_FORMATTER.format(weekStart)} – ${WEEK_RANGE_FORMATTER.format(weekEnd)}, ${weekEnd.getFullYear()}`
}

export { MOBILE_QUERY, isMobileViewportInitial } from '../Common/useIsMobile'


